import { ApiError, fetchCurrentUser, UserProfile } from "../utils/api";
import { AuthTokens, CognitoError, refreshSession } from "./cognito";
import {
  clearAuthCookies,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "./cookies";

export type SessionResult = { signedIn: true; user: UserProfile | null } | { signedIn: false };

const SIGNED_OUT: SessionResult = { signedIn: false };

/** Save freshly issued tokens to the cookies. */
export function storeTokens(tokens: AuthTokens) {
  setAccessToken(tokens.accessToken, tokens.expiresIn);
  if (tokens.refreshToken) setRefreshToken(tokens.refreshToken);
}

/**
 * Trade the refresh token for a new access token. A rejected refresh token clears the
 * cookies; a network failure keeps them so the next page load can try again.
 */
async function tryRefresh(refreshToken: string): Promise<string | null> {
  try {
    const tokens = await refreshSession(refreshToken);
    storeTokens(tokens);
    return tokens.accessToken;
  } catch (err) {
    if (err instanceof CognitoError) clearAuthCookies();
    return null;
  }
}

/**
 * Work out whether the visitor is signed in from the cookies, by fetching their profile.
 * - 200 → signed in; 404 → signed in, no profile row yet
 * - 401 → refresh once and retry; otherwise clear the cookies
 * - network error / 5xx → signed out for now, cookies kept
 */
export async function resolveSession(): Promise<SessionResult> {
  let accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (!accessToken && !refreshToken) return SIGNED_OUT;

  let refreshed = false;
  if (!accessToken) {
    accessToken = await tryRefresh(refreshToken as string);
    refreshed = true;
    if (!accessToken) return SIGNED_OUT;
  }

  for (;;) {
    try {
      const user = await fetchCurrentUser(accessToken);
      return { signedIn: true, user };
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) return SIGNED_OUT;
      if (refreshToken && !refreshed) {
        refreshed = true;
        accessToken = await tryRefresh(refreshToken);
        if (accessToken) continue;
        return SIGNED_OUT;
      }
      clearAuthCookies();
      return SIGNED_OUT;
    }
  }
}

/**
 * Finish a sign-in: store the tokens and load the profile. The tokens were just issued, so a
 * profile lookup that fails for any reason but a rejected token still counts as signed in.
 */
export async function completeSignIn(tokens: AuthTokens): Promise<UserProfile | null> {
  storeTokens(tokens);
  try {
    return await fetchCurrentUser(tokens.accessToken);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      clearAuthCookies();
      throw err;
    }
    return null;
  }
}
